/**
 * EncouragementModal
 * Displays encouragement messages with feedback options
 */

import React from 'react';
import {
  View,
  Text,
  Modal,
  StyleSheet,
  TouchableOpacity,
  Dimensions,
  Platform,
} from 'react-native';
import { EncouragementMessage, FeedbackRating } from '../models/EncouragementMessage';
import { getPatternDescription } from '../services/encouragement/PatternDetector';

interface EncouragementModalProps {
  message: EncouragementMessage | null;
  visible: boolean;
  onDismiss: () => void;
  onFeedback: (messageId: string, rating: FeedbackRating) => void;
}

export const EncouragementModal: React.FC<EncouragementModalProps> = ({
  message,
  visible,
  onDismiss,
  onFeedback,
}) => {
  if (!message) return null;

  const handleThumbsUp = () => {
    onFeedback(message.id, 'thumbs_up');
  };

  const handleThumbsDown = () => {
    onFeedback(message.id, 'thumbs_down');
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onDismiss}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Pattern type */}
          <Text style={styles.patternText}>
            {getPatternDescription(message.pattern)}
          </Text>

          {/* Message */}
          <Text style={styles.messageText}>{message.message}</Text>

          {/* Feedback buttons */}
          <View style={styles.feedbackContainer}>
            <TouchableOpacity
              style={styles.feedbackButton}
              onPress={handleThumbsUp}
              accessibilityLabel="Thumbs up"
              accessibilityHint="Mark this message as helpful"
            >
              <Text style={styles.feedbackIcon}>👍</Text>
              <Text style={styles.feedbackLabel}>Helpful</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.feedbackButton}
              onPress={handleThumbsDown}
              accessibilityLabel="Thumbs down"
              accessibilityHint="Mark this message as not helpful"
            >
              <Text style={styles.feedbackIcon}>👎</Text>
              <Text style={styles.feedbackLabel}>Not helpful</Text>
            </TouchableOpacity>
          </View>

          {/* Dismiss button */}
          <TouchableOpacity
            style={styles.dismissButton}
            onPress={onDismiss}
            accessibilityLabel="Dismiss message"
          >
            <Text style={styles.dismissText}>Dismiss</Text>
          </TouchableOpacity>
        </View>
      </View>
    </Modal>
  );
};

const { width } = Dimensions.get('window');

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modalContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    padding: 24,
    width: Math.min(width - 40, 400),
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
      },
      android: {
        elevation: 8,
      },
      web: {
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      },
    }),
  },
  patternText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#007AFF',
    marginBottom: 12,
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  messageText: {
    fontSize: 18,
    lineHeight: 26,
    color: '#333333',
    textAlign: 'center',
    marginBottom: 24,
    fontWeight: '400',
  },
  feedbackContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 16,
    marginBottom: 20,
  },
  feedbackButton: {
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 12,
    backgroundColor: '#F5F5F5',
    minWidth: 100,
  },
  feedbackIcon: {
    fontSize: 32,
    marginBottom: 4,
  },
  feedbackLabel: {
    fontSize: 12,
    color: '#666666',
    fontWeight: '500',
  },
  dismissButton: {
    alignItems: 'center',
    padding: 12,
  },
  dismissText: {
    fontSize: 14,
    color: '#999999',
    fontWeight: '500',
  },
});
